import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetSupportedObjectsQuery } from '../../../store/integrations/integrations'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useFormContext } from 'react-hook-form'
import { FormRow, FormValues } from './CrmField'
import { FieldIntegrationRow } from './AIGeneratedFields'
import { generateId } from '../../../common/util'

interface MeetingDataTabProps {
  connector: string
}

const meetingVariableOptions = [
  { id: 'meetingStartDate', label: 'Meeting Start Date' },
  { id: 'meetingEndDate', label: 'Meeting End Date' },
]

export const MeetingDataTab: React.FC<MeetingDataTabProps> = ({ connector }) => {
  const accountId = useAppSelector(selectAccountId)
  const { watch, setValue } = useFormContext<FormValues>()
  const formFields = watch('mappingFormFields')
  const variableFields = formFields.filter((field) => field.type === 'variable' && field.value !== 'contactId')
  const { data: supportedObjects, isFetching: isSupportedObjectsLoading } = useGetSupportedObjectsQuery({
    accountId,
    connector: connector,
  })

  const handleAddVariableClick = () => {
    const newFormFields = formFields
    newFormFields.push({ key: generateId(), objectName: null, fieldName: null, type: 'variable', value: null })
    setValue('mappingFormFields', newFormFields)
  }

  if (!supportedObjects) {
    return null
  }
  return (
    <div>
      <div>Add meeting information beyond what AI extracts from conversations.</div>

      <div className='mb-2' style={{ color: '#7949FF', cursor: 'pointer', fontWeight: 500 }} onClick={handleAddVariableClick}>
        + Add variable
      </div>

      {variableFields.map((field, index) => {
        return <MeetingDataVariableRow key={index} connector={connector} field={field} index={index} />
      })}
    </div>
  )
}

type MeetingDataVariableRowProps = {
  connector: string
  field: FormRow
  index: number
}

const MeetingDataVariableRow: React.FC<MeetingDataVariableRowProps> = ({ connector, field, index }) => {
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null)
  const { watch, setValue } = useFormContext<FormValues>()
  const formFields = watch('mappingFormFields')
  const variableFields = formFields.filter((field) => field.type === 'variable' && field.value !== 'contactId')
  const selectedObjects = formFields.map((field) => field.objectName).filter((value): value is string => value !== null)
  useEffect(() => {
    meetingVariableOptions.forEach((option) => {
      if (option.id === field.value) {
        return option
      }
    })
  }, [meetingVariableOptions, field])

  useEffect(() => {
    meetingVariableOptions.forEach((option) => {
      if (option.id === field.value) {
        setSelectedOption(option)
      }
    })
  }, [field, meetingVariableOptions])

  const handleOptionSelected = (option: SelectOption) => {
    variableFields[index] = { ...variableFields[index], value: option.id }
    const newVariableFields = [...variableFields]
    const newFormFields = formFields.filter((formField) => formField.type !== 'variable').concat(newVariableFields)
    setSelectedOption(option)
    setValue('mappingFormFields', newFormFields)
  }

  return (
    <div>
      <div className='d-flex align-items-center mb-3'>
        <div className='d-flex align-items-center mb-3'>
          <SelectDropdown
            className='zocks-px-0.75 py-2'
            options={meetingVariableOptions}
            initialSelectedOption={selectedOption}
            onOptionSelected={handleOptionSelected}
            searchable
            placeholder='Select variable...'
          />
        </div>

        <div className='d-flex align-items-center mb-3'>→</div>

        <FieldIntegrationRow formRow={field} type='variable' connector={connector} showFieldTitle={false} selectedObjects={selectedObjects} />
      </div>
    </div>
  )
}
